import { useAuth } from '@agentnet/auth';
import { pxToRem } from '@fluentsms/agentnet-web-components';
import {
  Collapse,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Popover,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { grey } from '@material-ui/core/colors';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { ChevronRightOutlined, ExpandMore } from '@material-ui/icons';
import { getAgentNetMarketingUrl } from 'api/host-by-env';
import clsx from 'clsx';
import ReportsAndPayDrawer from 'core/layout-core/core-content/ReportsAndPayDrawer';
import { OrderType } from 'features/orderManagement/types';
import { report } from 'features/Remittance/reportsListConfig';
import useRatesAndFee from 'features/Remittance/useRatesAndFee';
import { FileDataContext, FileDataContextInterface } from 'hooks/FileDataContext';
import { FincenReportContext, FincenReportContextInterface } from 'hooks/FincenReportContext';
import { useAnmRedirect } from 'hooks/useAnmRedirect';
import React, { MouseEvent, useContext, useEffect, useMemo, useState } from 'react';
import { generatePath, Link, matchPath, useHistory, useLocation } from 'react-router-dom';
import LoadingSpinner from 'ui-kit/components/LoadingSpinner';
import { CollapseArrow, ExpandArrow } from 'ui-kit/icons/AgentNetIcon';
import { useViewState } from '../../hooks/ViewStateContext';
import { NavBadge } from './NavBadge';
import { NavLinkText } from './NavLinkTexts';
import { NavLinkType } from './NavLinkType';

interface LeftNavProps {
  localWindow?: () => Window;
  appSwitcher?: boolean;
  onLinkClick?: () => void;
  menuCollapsed: boolean;
  setMenuCollapse: (menuCollapsed: boolean) => void;
  navState: NavLinkType[];
  setNavState: (navlist: NavLinkType[]) => void;
  pathParams?: any;
  topMargin?: number;
  children?: React.ReactNode;
  hasPermission: any;
  userFirm: any;
  prismUserProfile: any;
  fileData: any;
  isFormDirty?: boolean;
  setRatesAndFeeDrawerClicked?: (val: boolean) => void;
  setOpenRatesAndFeeDrawer?: (val: boolean) => void;
  openRatesAndFeeDrawer?: boolean;
  setOpenSaveModal?: (val: boolean) => void;
}

const reportAndReviewTabs = ['Report', 'Review'];
const LeftNav = (props: LeftNavProps): JSX.Element => {
  const {
    localWindow,
    menuCollapsed,
    setMenuCollapse,
    navState,
    setNavState,
    topMargin,
    children,
    hasPermission,
    userFirm,
    prismUserProfile,
    fileData,
    isFormDirty,
    setRatesAndFeeDrawerClicked,
    openRatesAndFeeDrawer,
    setOpenRatesAndFeeDrawer,
    setOpenSaveModal,
  } = props;
  const history = useHistory();
  const [drawerWidth, setDrawerWidth] = useState(menuCollapsed ? 72 : 311);
  const [popperEl, setPopperEl] = useState<any>(null);
  const [openedPopperId, setOpenedPopperId] = useState<any>(null);
  const shownCategories: { [key: string]: boolean } = {};
  const { routerParamValue } = useViewState();
  const useStyles = makeStyles((theme: Theme) =>
    createStyles({
      drawer: {
        [theme.breakpoints.up('sm')]: {
          width: drawerWidth,
          flexShrink: 0,
        },
      },
      drawerPaper: {
        backgroundColor: theme.palette.brand.feather25,
        width: drawerWidth,
        top: topMargin ? topMargin : 113,
        height: `calc(100% - 51px)`,
        zIndex: 10,
      },
      list: {
        padding: 0,
        color: theme.palette.text.primary,
        marginTop: children ? theme.spacing(1) : theme.spacing(3),
        marginBottom: 113,
      },
      listCollapsed: {
        '& .MuiListItem-button': {
          paddingRight: 0,
          justifyContent: 'center',
          paddingLeft: theme.spacing(2),
        },
      },
      linkItemWrap: {
        textDecoration: 'none',
      },
      linkItem: {
        color: theme.palette.text.primary,
        paddingTop: theme.spacing(1),
        paddingBottom: theme.spacing(1),
        paddingLeft: theme.spacing(2),
        transition: '.3s all',
        '&:hover': {
          backgroundColor: 'transparent',
        },
        '&>div svg path': {
          fill: grey[500],
        },
        '&:hover>div svg path': {
          fill: grey[800],
        },
      },
      disabledLink: {
        pointerEvents: 'none',
        opacity: 0.5,
      },
      listItemTypeParent: {
        display: 'flex',
        alignItems: 'center',
        flexGrow: 1,
      },
      listItemType: {
        fontWeight: `500 !important` as any,
        color: grey[800],
        '& span': {
          fontWeight: `400 !important` as any,
          color: grey[800],
        },
      },
      linkItemCollapsed: {
        paddingRight: 0,
        alignItems: 'center',
        justifyContent: 'center',
      },
      linkItemText: {
        margin: 0,
        '&>span': { fontWeight: 400, color: grey[800] },
      },
      subLinkItem: {
        padding: `4px 24px 4px 40px`,
        fontSize: pxToRem(14),
        textDecoration: 'none',
        fontWeight: 400,
        '&>div': {
          padding: '4px 14px',
          transition: '.3s all',
          borderRadius: '4px',
        },
        '&:hover': {
          background: 'transparent',
        },
        '&:focus': {
          background: 'transparent',
        },
        '&:hover>div': {
          backgroundColor: theme.palette.primary.light,
        },
      },
      subLinkItemPopover: {
        paddingLeft: theme.spacing(3),
      },
      linkItemActive: {
        '& span': {
          fontWeight: `600 !important` as any,
          color: theme.palette.text.primary,
        },
      },
      sublinkItemActive: {
        '& span': {
          fontWeight: `600 !important` as any,
          color: grey[800],
        },
        '&>div>div': {
          backgroundColor: theme.palette.primary.light,
        },
      },
      subLinkItemText: {
        margin: 0,
        '& .MuiListItemText-primary': {
          color: theme.palette.text.secondary,
          fontSize: pxToRem(14),
          lineHeight: pxToRem(17),
          fontWeight: 400,
        },
      },
      subLinkHeader: {
        paddingLeft: theme.spacing(2),
        paddingTop: theme.spacing(1),
        paddingBottom: theme.spacing(1),
      },
      popoverPaper: {
        minWidth: 250,
        overflow: 'visible',
      },
      popoverContent: {
        '&:before': {
          content: '""',
          position: 'absolute',
          top: 'calc(16px)',
          left: '-15px',
          borderWidth: '10px',
          borderStyle: 'solid',
          borderColor: 'transparent white transparent transparent',
        },
        paddingTop: theme.spacing(2),
        paddingBottom: theme.spacing(2),
      },
      listItemIcon: {
        minWidth: '32px',
      },
      listItemIconActive: {
        '&>svg path': {
          fill: `${grey[800]} !important` as any,
        },
      },
      p0: {
        padding: 0,
      },
      navBottomWrap: {
        margin: `auto`,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'center',
        borderTop: '1px solid',
        borderColor: theme.palette.divider,
        width: '100%',
      },
      navBottomWrapNotCollapsed: {
        position: 'fixed',
        zIndex: 1,
        width: drawerWidth,
        bottom: 0,
      },
      navBottomWrapCollapsed: {
        marginBottom: 63,
      },
      toggleWrap: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: 'calc(100% - 1px)',
        backgroundColor: theme.palette.brand.feather25,
        height: 51,
      },
      toggleWrapCollapsed: {
        flexDirection: 'column-reverse',
        height: 49,
      },
      collapseToggleIconBtn: {
        marginRight: pxToRem(2),
        '&:hover': {
          background: 'transparent',
        },
      },
      categoryTitleWrap: {
        marginTop: theme.spacing(1),
        borderTop: '1px solid',
        borderTopColor: theme.palette.divider,
        '&#category-0': {
          borderTop: 'none',
          marginTop: 0,
        },
      },
      categoryTitle: {
        color: theme.palette.primary.main,
        width: '100%',
        display: 'block',
        padding: '8px 0px 0px 16px',
      },
      categoryTitleCollapsed: {
        padding: '8px 0',
        textAlign: 'center',
      },
      collapseNavText: {
        fontWeight: 500,
        color: grey[800],
        lineHeight: 1,
        marginLeft: theme.spacing(1),
        textTransform: 'none',
      },
    }),
  );
  useEffect(() => {
    setDrawerWidth(menuCollapsed ? 72 : 311);
  }, [menuCollapsed]);

  const classes = useStyles({ drawerWidth, topMargin });
  const container = localWindow !== undefined ? () => localWindow().document.body : undefined;
  const routerLocation = useLocation<{ order: OrderType }>();
  const isOrderManagementPath = location.pathname.includes('order-management') && routerLocation.state;

  const fileDataCtx: FileDataContextInterface = useContext(FileDataContext) ?? {};
  const { setTabValue, loadFileData, triggerFileRefresh } = fileDataCtx;
  const [isLoading, setIsLoading] = useState(true);
  const fincenReportCtx = useContext<FincenReportContextInterface | null>(FincenReportContext);
  const { setPendingNavigation } = fincenReportCtx ?? {};

  const [isApprovedAttorneyAccount, setIsApprovedAttorneyAccount] = useState<boolean>(false);
  const manageUserSubLinkText = NavLinkText.ManageUsers.toLocaleLowerCase();
  const usersLinkText = NavLinkText.Users.toLocaleLowerCase();
  useEffect(() => {
    const accountNumber = parseInt(window.sessionStorage.getItem('accountNumber') || '0', 10);
    if (fileData && userFirm?.offices)
      for (const office of userFirm.offices)
        for (const account of office.accounts)
          if (accountNumber === account.accountId) {
            setIsApprovedAttorneyAccount(account.clientType === 'Approved Attorney');
            break;
          }
  }, [fileData]);

  useEffect(() => {
    if (hasPermission) {
      setIsLoading(false);
    }
  }, [hasPermission]);

  useEffect(() => {
    const newNavState = navState.map((link) => {
      if (link.linkText === 'FinCEN Real Estate Report' && location.pathname.includes('/fincen-reporting/')) {
        return {
          ...link,
          expanded: true,
        };
      }
      return {
        ...link,
        expanded: location.pathname.startsWith(link.path) && location.pathname !== link.path,
      };
    });
    setNavState(newNavState);
  }, [location.pathname]);

  const handleMenuCollapse = () => {
    setNavState(navState.map((obj) => ({ ...obj, expanded: false })));
    setMenuCollapse(!menuCollapsed);
  };

  const handlePopperClose = () => {
    setPopperEl(null);
    setOpenedPopperId(null);
  };

  const handleParentClick = (event: any, link: any, idx: any) => {
    if (!menuCollapsed) {
      setNavState(navState.map((obj) => (obj.linkText === link.linkText ? { ...obj, expanded: !obj.expanded } : obj)));
    } else {
      setPopperEl(event.currentTarget);
      setOpenedPopperId(idx);
    }
  };

  //Rates and Fees Logic
  const { drawerData, setDrawerData, openDrawer, setOpenDrawer, handleDrawerOpen, rateAndFeeExecute } = useRatesAndFee(
    userFirm?.firmId ?? '',
    report,
  );

  const handleRatesandFees = (openCalcCriteria = false) => {
    const hasDataList = [
      fileData?.serviceStatus?.hasRemittableCpl,
      fileData?.serviceStatus?.hasActiveJacket,
      fileData?.serviceStatus?.hasStandAloneEndorsement,
      fileData?.serviceStatus?.hasRemittedJacket,
      fileData?.serviceStatus?.hasActiveCpl,
      fileData?.serviceStatus?.hasRemittedCpl,
    ];

    handleDrawerOpen(
      {
        data: {
          fileId: fileData?.fileId,
          fileNumber: fileData?.fileNumber,
          firmId: fileData?.firmId,
          underwriter: fileData?.underwriterCode,
          isReopened: fileData?.serviceStatus?.isReopenedForRemit,
          isReopenedByProduct: fileData?.serviceStatus?.isReopenedByProduct,
        },
      },
      openCalcCriteria
        ? 'review'
        : fileData?.serviceStatus?.isPricingValid || !hasDataList.includes(true)
        ? 'report'
        : 'review',
    );
  };

  const onReport = () => {
    // Trigger refresh across ALL file tabs
    triggerFileRefresh?.(true);
  };

  const isDisabled = useMemo(() => !fileData?.fileId, [fileData?.fileId]);

  //Property Reports Logic
  const [isPending, setPendingFlag] = useState(false);
  const { isEmployee } = useAuth();
  const { ensurePrismUserExists } = useAnmRedirect();

  const handlePropertyReports = async () => {
    if (isPending) {
      return;
    }

    setPendingFlag(true);
    const response = await ensurePrismUserExists(prismUserProfile);
    setPendingFlag(false);

    if (response) {
      let url = getAgentNetMarketingUrl();
      if (isEmployee()) {
        url += 'b2e';
      }
      window.open(url, '_blank');
    }
  };

  const handleTopLevelClick = (link: NavLinkType) => {
    if (link.path.includes('#ratesfees')) {
      if (isFormDirty) {
        setRatesAndFeeDrawerClicked?.(true);
        setOpenSaveModal?.(true);
      } else {
        window.history.replaceState({}, '', `#ratesfees`);
        handleRatesandFees();
      }
    }
    if (link.path.includes('/services/property-reports')) {
      handlePropertyReports();
    }
    return;
  };

  const handleFincenSublinkClick = (event: MouseEvent<HTMLAnchorElement>, sublinkPath: string) => {
    const currentPath = location.pathname;
    const isFincenSection = currentPath.includes('/fincen-reporting/');
    const isNavigatingToFincen = sublinkPath.includes('/fincen-reporting/');
    const isSamePath = currentPath === sublinkPath;
    const reportStatusCdId = fincenReportCtx?.fincenReport?.reportStatusCdId;

    // When report is in generated state (9302), allow immediate navigation
    if (isFincenSection && isNavigatingToFincen && !isSamePath && setPendingNavigation && reportStatusCdId !== 9302) {
      event.preventDefault();
      setPendingNavigation(sublinkPath);
    }
  };

  useEffect(() => {
    if (openRatesAndFeeDrawer) {
      setOpenRatesAndFeeDrawer?.(false);
      window.history.replaceState({}, '', `#ratesfees`);
      handleRatesandFees();
    }
  }, [openRatesAndFeeDrawer]);

  useEffect(() => {
    const currentState: any = routerLocation?.state;
    const isRateAndFeePath = routerLocation.hash.includes(`#ratesfees`);
    routerLocation.pathname.includes('/dashboards/service-orders') ? setMenuCollapse(true) : setMenuCollapse(false);
    if (currentState?.showRateAndFeeDrawer && isRateAndFeePath) {
      handleRatesandFees(true);
      window.history.replaceState({}, '');
    }
  }, [routerLocation?.state]);

  const hideMenu: boolean = hasPermission.staffAdmin;

  const processedNavState = useMemo(() => {
    return navState.map((link) => {
      if (link.linkText === NavLinkText.Billing && !hasPermission.autopay) {
        const { sublinks, ...flatLink } = link;
        return { ...flatLink, expanded: false };
      }
      return link;
    });
  }, [navState, hasPermission.autopay]);

  const hideSubMenu = (sublink: any) => {
    const hideReportAndPay =
      (sublink?.linkText === 'Pay' && !hasPermission.pay) ||
      (!hasPermission.remit && reportAndReviewTabs.includes(sublink?.linkText)) ||
      (sublink.linkText === 'Service Order Invoices' && !hasPermission.pay && !hasPermission.remit);
    const hideFormManagement =
      sublink?.linkText === NavLinkText.FormManagement && hasPermission.inventoryManagementViewOnly;
    return sublink.isHidden || hideReportAndPay || hideFormManagement;
  };

  if (isLoading) {
    return <></>;
  }

  return (
    <>
      {hideMenu ? null : (
        <div>
          <LoadingSpinner status={isPending ? 'pending' : 'success'} variant="linear" />
          <nav className={classes.drawer}>
            <Drawer variant="permanent" classes={{ paper: classes.drawerPaper }} anchor="left" container={container}>
              {children && !menuCollapsed && <>{children}</>}
              <List className={clsx(classes.list, { [classes.listCollapsed]: menuCollapsed })}>
                {processedNavState.map((link, idx) => {
                  const filesLink = link.path.includes(':fileId')
                    ? routerParamValue
                      ? generatePath(link.path, { fileId: routerParamValue })
                      : link.path // Keep the path with :fileId placeholder when routerParamValue is empty
                    : location.href.includes('#ratesfees') && link.path === '#ratesfees'
                    ? location.pathname + '#ratesfees'
                    : link.path;
                  if (
                    link.isHidden ||
                    (link.linkText === NavLinkText.ReportPay &&
                      !hasPermission.pay &&
                      !hasPermission.remit &&
                      !hasPermission.payOnly) ||
                    (link.linkText === NavLinkText.Documents && !hasPermission.documents) ||
                    (link.linkText === NavLinkText.CPL && !hasPermission.cpl) ||
                    (link.linkText === NavLinkText.Jackets && !hasPermission.jackets) ||
                    (link.linkText.toLowerCase() === usersLinkText && !hasPermission.manageUsers) ||
                    (link.linkText === NavLinkText.Billing && !hasPermission.billing) ||
                    (link.linkText === NavLinkText.BackTitle && !hasPermission.canRunBackTitleFiles) ||
                    (link.linkText === NavLinkText.BackTitleSearch && !hasPermission.canRunBackTitleAdHoc) ||
                    (link.linkText === NavLinkText.AddOns && !hasPermission.marketplace) ||
                    (link.linkText === NavLinkText.InventoryManagement &&
                      !hasPermission.inventoryManagement &&
                      !hasPermission.inventoryManagementViewOnly) ||
                    (link.linkText === NavLinkText.OracleDowntimeSchedule && !hasPermission.orderManagementAdmin)
                  )
                    return null;

                  const renderNavItems = link.sublinks ? (
                    <React.Fragment key={idx}>
                      <ListItem className={classes.linkItem} button onClick={(e) => handleParentClick(e, link, idx)}>
                        {!menuCollapsed ? (
                          <>
                            <ListItemIcon
                              className={clsx(classes.listItemIcon, {
                                [classes.listItemIconActive]:
                                  location.pathname === filesLink || location.pathname.includes(link.path),
                              })}
                            >
                              {link.icon}
                            </ListItemIcon>
                            <Typography variant="h5" className={clsx(classes.listItemType, classes.listItemTypeParent)}>
                              <ListItemText
                                primary={link.linkText}
                                data-testid={link.dataTestId ? link.dataTestId : link.testId}
                                className={clsx({
                                  [classes.linkItemActive]:
                                    location.pathname === filesLink || location.pathname.includes(link.path),
                                })}
                              />
                              {!link.expanded && link.badgeContentFunction && (
                                <span style={{ marginRight: 6 }}>
                                  <NavBadge link={link} />
                                </span>
                              )}
                              {link.expanded ? (
                                <ExpandMore color="secondary" />
                              ) : (
                                <ChevronRightOutlined color="secondary" />
                              )}
                            </Typography>
                          </>
                        ) : (
                          <Tooltip arrow placement="right" title={link.linkText}>
                            <div>
                              <ListItemIcon
                                className={clsx(classes.listItemIcon, {
                                  [classes.listItemIconActive]: location.pathname === filesLink,
                                })}
                                onClick={(e) => setPopperEl(e.currentTarget)}
                              >
                                {link.icon}
                              </ListItemIcon>
                              <NavBadge link={link} shouldShowDot />
                            </div>
                          </Tooltip>
                        )}
                      </ListItem>
                      {!menuCollapsed ? (
                        <Collapse key={'SubListOf_' + link.linkText} in={link.expanded}>
                          <List disablePadding>
                            {link.sublinks.map((sublink) => {
                              if (hideSubMenu(sublink)) return null;
                              const sublinkTo = sublink.path.includes(':fileId')
                                ? routerParamValue
                                  ? generatePath(sublink.path, { fileId: routerParamValue })
                                  : sublink.path // Keep the path with :fileId placeholder when routerParamValue is empty
                                : sublink.path;
                              const isActive = !!matchPath(location.pathname, {
                                path: sublinkTo,
                                exact: true,
                                strict: false,
                              });
                              return (
                                <Link
                                  to={sublinkTo}
                                  className={clsx(classes.p0, classes.linkItemWrap, {
                                    [classes.sublinkItemActive]: isActive,
                                    [classes.disabledLink]:
                                      !hasPermission.hasFirmId &&
                                      hasPermission.behalfOfUser &&
                                      sublink.linkText.toLocaleLowerCase() !== manageUserSubLinkText,
                                  })}
                                  key={sublink.linkText}
                                  data-testid={sublink.dataTestId ? sublink.dataTestId : sublink.testId}
                                  aria-label={sublink.linkText}
                                  onClick={(e) => handleFincenSublinkClick(e, sublinkTo)}
                                >
                                  <ListItem className={classes.subLinkItem} button disableRipple disableTouchRipple>
                                    <ListItemText className={classes.subLinkItemText} primary={sublink.linkText}>
                                      <Typography variant="h5" className={clsx(classes.listItemType)}>
                                        {sublink.linkText}
                                      </Typography>
                                    </ListItemText>
                                    <NavBadge link={sublink} shouldShowDot={menuCollapsed} />
                                  </ListItem>
                                </Link>
                              );
                            })}
                          </List>
                        </Collapse>
                      ) : (
                        <Popover
                          open={openedPopperId === idx}
                          id={'popover-' + idx}
                          anchorEl={popperEl}
                          onClose={handlePopperClose}
                          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                          PaperProps={{ square: true, className: classes.popoverPaper }}
                        >
                          <List disablePadding className={classes.popoverContent}>
                            <Typography variant="h5" className={classes.subLinkHeader}>
                              {link.linkText}
                            </Typography>
                            {link.sublinks.map((sublink, index) => {
                              if (hideSubMenu(sublink)) return null;
                              const sublinkTo = sublink.path.includes(':fileId')
                                ? routerParamValue
                                  ? generatePath(sublink.path, { fileId: routerParamValue })
                                  : sublink.path // Keep the path with :fileId placeholder when routerParamValue is empty
                                : sublink.path;
                              const isActive = !!matchPath(location.pathname, {
                                path: sublinkTo,
                                exact: true,
                                strict: false,
                              });
                              return (
                                <Link
                                  to={sublinkTo}
                                  className={clsx(classes.p0, classes.linkItemWrap, {
                                    [classes.sublinkItemActive]: isActive,
                                    [classes.disabledLink]:
                                      !hasPermission.hasFirmId &&
                                      hasPermission.behalfOfUser &&
                                      sublink.linkText !== manageUserSubLinkText,
                                  })}
                                  key={index}
                                  data-testid={sublink.dataTestId ? sublink.dataTestId : sublink.testId}
                                >
                                  <ListItem
                                    className={clsx(classes.subLinkItem, classes.subLinkItemPopover)}
                                    button
                                    disableRipple
                                    disableTouchRipple
                                  >
                                    <ListItemText className={classes.subLinkItemText} primary={sublink.linkText} />
                                    <NavBadge link={sublink} />
                                  </ListItem>
                                </Link>
                              );
                            })}
                          </List>
                        </Popover>
                      )}
                    </React.Fragment>
                  ) : (
                    <Link
                      to={filesLink}
                      className={clsx(classes.linkItemWrap, {
                        [classes.linkItemActive]: location.pathname === filesLink,
                        [classes.disabledLink]:
                          (isDisabled && location.pathname.includes('/files/')) ||
                          (!hasPermission.hasFirmId &&
                            hasPermission.behalfOfUser &&
                            link.linkText.toLocaleLowerCase() !== usersLinkText),
                      })}
                      data-testid={link.dataTestId ? link.dataTestId : link.testId}
                      onClick={() => handleTopLevelClick(link)}
                    >
                      {isOrderManagementPath ? (
                        menuCollapsed && (
                          <ListItem className={classes.linkItem} button key={link.linkText}>
                            <ListItemIcon
                              className={clsx(classes.listItemIcon, {
                                [classes.listItemIconActive]: location.pathname === filesLink,
                              })}
                            >
                              {link.icon}
                            </ListItemIcon>
                            <NavBadge link={link} shouldShowDot={menuCollapsed} />
                          </ListItem>
                        )
                      ) : (
                        <ListItem className={classes.linkItem} button key={link.linkText}>
                          {!menuCollapsed ? (
                            <>
                              <ListItemIcon
                                className={clsx(classes.listItemIcon, {
                                  [classes.listItemIconActive]: location.pathname === filesLink,
                                })}
                              >
                                {link.icon}
                              </ListItemIcon>
                              <Typography
                                variant="h5"
                                className={clsx(classes.listItemType, {
                                  [classes.linkItemActive]: location.pathname === filesLink,
                                })}
                              >
                                <ListItemText className={classes.linkItemText} primary={link.linkText} />
                              </Typography>
                            </>
                          ) : (
                            <Tooltip arrow placement="right" title={link.linkText}>
                              <ListItemIcon
                                className={clsx(classes.listItemIcon, {
                                  [classes.listItemIconActive]: location.pathname === filesLink,
                                })}
                              >
                                {link.icon}
                              </ListItemIcon>
                            </Tooltip>
                          )}
                          <NavBadge link={link} shouldShowDot={menuCollapsed} />
                        </ListItem>
                      )}
                    </Link>
                  );

                  if (link.category && !shownCategories[link.category]) {
                    shownCategories[link.category] = true;
                    return (
                      <div key={idx} id={`category-${idx}`} className={classes.categoryTitleWrap}>
                        <Typography
                          variant="h6"
                          className={clsx(classes.categoryTitle, { [classes.categoryTitleCollapsed]: menuCollapsed })}
                        >
                          {link.category}
                        </Typography>
                        {renderNavItems}
                      </div>
                    );
                  }

                  return renderNavItems;
                })}
              </List>
              <div
                className={clsx(classes.navBottomWrap, {
                  [classes.navBottomWrapNotCollapsed]: !menuCollapsed,
                  [classes.navBottomWrapCollapsed]: menuCollapsed,
                })}
              >
                <div className={clsx(classes.toggleWrap, { [classes.toggleWrapCollapsed]: menuCollapsed })}>
                  <IconButton
                    aria-label="Collapse Menu Toggle"
                    disableFocusRipple
                    disableRipple
                    disableTouchRipple
                    onClick={handleMenuCollapse}
                    className={classes.collapseToggleIconBtn}
                  >
                    {menuCollapsed ? (
                      <ExpandArrow />
                    ) : (
                      <>
                        <CollapseArrow />
                        <Typography variant="h5" className={classes.collapseNavText}>
                          Collapse Nav
                        </Typography>
                      </>
                    )}
                  </IconButton>
                </div>
              </div>
            </Drawer>
          </nav>
          <ReportsAndPayDrawer
            isFromFile
            openDrawer={openDrawer}
            loadFileData={loadFileData}
            disableFields={fileData?.fileStatus !== 'Open'}
            onReport={onReport}
            setOpenDrawer={setOpenDrawer}
            drawerData={drawerData}
            setFileTab={setTabValue}
            setDrawerData={setDrawerData}
            rateAndFeeExecute={rateAndFeeExecute}
          />
        </div>
      )}
    </>
  );
};

export default LeftNav;
